import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import settingsApi, { BRAND_CACHE_KEY, padContactTeam, readJsonCache } from '../services/settingsApi';

const SiteBrandContext = createContext(null);

const DEFAULTS = {
  websiteLogo: '/Flare Logo.png',
  websiteFooterLogo: '/Flare Logo.png',
  websiteLoadingLogo: '/Flare Logo.png',
  userPanelLogo: '/Flare Logo.png',
  websiteDomain: 'digital.flare.pk',
  websitePhone: '042-111-123-456',
  websiteEmail: 'info@digital.flare.pk',
  websiteWhatsapp: '9242111123456',
  socialFacebook: 'https://facebook.com/flaremagonline',
  socialInstagram: 'https://instagram.com/flaremagonline',
  socialYoutube: 'https://youtube.com/@flaremagonline',
  socialTwitter: '',
  socialWhatsapp: '',
  officeCardTitle: 'Visit Our Office',
  officeCardNote: 'By appointment only',
  officeCardLocation: 'Gulberg III, Lahore, Pakistan',
  officeMapUrl: 'https://maps.app.goo.gl/nfMM1mceaq3LndTC6',
  officeHqTitle: 'Flare Magazine HQ',
  officeHqNote: 'Visit us by appointment',
  officeHqAddress: '2nd Floor, Digital Flare Network, Main Boulevard, Gulberg III, Lahore, 54660, Pakistan',
  contactTeam: [],
};

function readCachedBrand() {
  const cached = readJsonCache(BRAND_CACHE_KEY);
  if (!cached) return null;
  return { ...DEFAULTS, ...cached };
}

export function SiteBrandProvider({ children }) {
  const cached = readCachedBrand();
  const [brand, setBrand] = useState(cached || DEFAULTS);
  const [loading, setLoading] = useState(!cached);

  const refresh = useCallback(async () => {
    try {
      const b = await settingsApi.getPublicBranding();
      setBrand((prev) => ({ ...prev, ...b }));
      return b;
    } catch (e) {
      console.warn('Public branding load failed', e);
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const b = await settingsApi.getPublicBranding();
        if (cancelled) return;
        setBrand((prev) => ({ ...prev, ...b }));
      } catch (e) {
        console.warn('Public branding load failed', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const value = useMemo(() => {
    const phone = brand.websitePhone || DEFAULTS.websitePhone;
    const email = brand.websiteEmail || DEFAULTS.websiteEmail;
    const waDigits = String(brand.websiteWhatsapp || DEFAULTS.websiteWhatsapp).replace(/\D/g, '');
    const telHref = `tel:${phone.replace(/[^\d+]/g, '')}`;
    const mailHref = `mailto:${email}`;
    const waHref = brand.socialWhatsapp || (waDigits ? `https://wa.me/${waDigits}` : '#');

    const team = padContactTeam(brand.contactTeam).filter((p) => (p.name || '').trim());
    const officeMapUrl = brand.officeMapUrl || DEFAULTS.officeMapUrl;
    const officeLocation = brand.officeCardLocation || DEFAULTS.officeCardLocation;

    return {
      ...DEFAULTS,
      ...brand,
      loading,
      refresh,
      phone,
      email,
      telHref,
      mailHref,
      waHref,
      domain: brand.websiteDomain || DEFAULTS.websiteDomain,
      logo: brand.websiteLogo || DEFAULTS.websiteLogo,
      footerLogo: brand.websiteFooterLogo || brand.websiteLogo || DEFAULTS.websiteFooterLogo,
      loadingLogo: brand.websiteLoadingLogo || brand.websiteLogo || DEFAULTS.websiteLoadingLogo,
      userLogo: brand.userPanelLogo || brand.websiteLogo || DEFAULTS.userPanelLogo,
      officeCardTitle: brand.officeCardTitle || DEFAULTS.officeCardTitle,
      officeCardNote: brand.officeCardNote || DEFAULTS.officeCardNote,
      officeCardLocation: officeLocation,
      officeMapUrl,
      officeHqTitle: brand.officeHqTitle || DEFAULTS.officeHqTitle,
      officeHqNote: brand.officeHqNote || DEFAULTS.officeHqNote,
      officeHqAddress: brand.officeHqAddress || DEFAULTS.officeHqAddress,
      contactTeam: team,
    };
  }, [brand, loading, refresh]);

  return <SiteBrandContext.Provider value={value}>{children}</SiteBrandContext.Provider>;
}

export function useSiteBrand() {
  const ctx = useContext(SiteBrandContext);
  if (!ctx) {
    return {
      ...DEFAULTS,
      loading: false,
      refresh: async () => null,
      phone: DEFAULTS.websitePhone,
      email: DEFAULTS.websiteEmail,
      telHref: 'tel:042111123456',
      mailHref: 'mailto:info@digital.flare.pk',
      waHref: 'https://wa.me/9242111123456',
      domain: DEFAULTS.websiteDomain,
      logo: DEFAULTS.websiteLogo,
      footerLogo: DEFAULTS.websiteFooterLogo,
      loadingLogo: DEFAULTS.websiteLoadingLogo,
      userLogo: DEFAULTS.userPanelLogo,
    };
  }
  return ctx;
}

export default SiteBrandContext;
